import { useEffect, useMemo } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons'
import { useMediaQuery } from 'react-responsive'
import { connect } from 'react-redux'
import cx from 'classnames'

import { Question as QuestionInterface } from '@commutifi-fe/interfaces'
import { Button as AppButton, Form, Progress, Typography, SpinnerThird } from '@commutifi-fe/ui'
import { selectors } from 'store/modules/enterpriseSurvey'
import { updateSurveyAnswers, upsertSurveyQuestions } from 'store/modules/enterpriseSurvey/sagas'
import { QuestionConfigStrategyFactory } from 'utils/helpers/surveys/questionsConfig'
import { CumulatedAnswers, PrefilledData, SurveyFormProps } from 'shared/interfaces/Survey'
import { FormattedMessage, useIntl } from 'locales/index'
import { getQuestionId, renderQuestion } from 'utils/helpers/surveys'
import { screenSizes } from 'constants/global'
import styles from './styles.module.scss'

// For aria description when user focus on the next step button
function AriaPercentageCompletion({ completionPercentage }: Pick<SurveyFormProps, 'completionPercentage'>) {
  return (
    <span style={{ display: 'none' }} id="progress" aria-hidden="false">
      {completionPercentage} completed
    </span>
  )
}

function MobileButton({
  completionPercentage,
  isLoading,
  currentStep
}: Pick<SurveyFormProps, 'completionPercentage' | 'isLoading'> & { currentStep: number }) {
  const progressFormat = () =>
    isLoading ? <SpinnerThird /> : <FontAwesomeIcon icon={faArrowRight} size="lg" className="u-color--white" />
  return (
    <div className={styles['button-container--fixed']}>
      <div className="u-flex-space-between u-flex-align-center u-flex-1">
        <div className="u-flex-column">
          {/* Related to 'hide' progress for Delta survey */}
          {/* <FormattedMessage
            id="onboarding.steps.button.percentageDone"
            values={{ percent: Math.round(completionPercentage) }}
          /> */}
          <Typography.Text size="lead" className="u-margin-top--tiny">
            <FormattedMessage id="global.step" /> {currentStep}
          </Typography.Text>
        </div>
        <AppButton
          aria-describedby="progress"
          type="link"
          style={{
            padding: 0,
            height: styles.progressButtonWidth,
            width: styles.progressButtonWidth
          }}
          htmlType="submit"
          disabled={isLoading}
        >
          <Progress
            type="circle"
            strokeColor="white"
            strokeWidth={4}
            size={parseInt('progressButtonWidth' in styles ? styles.progressButtonWidth : '52px')}
            // Related to 'hide' progress for Delta survey
            // percent={completionPercentage}
            format={progressFormat}
          />
          <AriaPercentageCompletion completionPercentage={completionPercentage} />
        </AppButton>
      </div>
    </div>
  )
}

function SectionForm({
  initialValidation,
  questions,
  nextSectionText,
  sectionAnswers = {},
  prefilledData = {},
  cumulatedAnswers = {},
  questionsByRefKey = {},
  onNextSection,
  upsertSurveyQuestions,
  updateSurveyAnswers,
  currentStep,
  completionPercentage,
  isLoading
}: SectionFormProps) {
  const intl = useIntl()
  const isXSmall = useMediaQuery({ maxWidth: screenSizes.xsMaxWidth })
  const [form] = Form.useForm()
  const { validateFields } = form

  useEffect(() => {
    if (initialValidation) {
      validateFields()
    }
  }, [validateFields, initialValidation])

  const questionsComponentProps = questions.map((question) => {
    const questionTypeStrategy = QuestionConfigStrategyFactory.getQuestionTypeStrategy(question.type)
    const questionComponentProps = questionTypeStrategy.processFormProps(
      question,
      prefilledData,
      cumulatedAnswers,
      questionsByRefKey
    )
    return {
      ...questionComponentProps,
      validation: questionTypeStrategy.getFieldValidation(question, {
        intl,
        props: (questionComponentProps as any).props
      }),
      question,
      questionId: getQuestionId(question)
    }
  })

  // Each time we change step, we verify if there is any value set to all questions.
  // If there is no value and there is a default value accessible we update our state
  // with those values to keep it up to date
  const initialValues = useMemo(() => {
    const initialValues = questionsComponentProps.reduce<Record<string, unknown>>((initialAnswers, questionProps) => {
      const initialAnswersCopy = { ...initialAnswers }
      if (
        !Array.isArray(questionProps.questionId) &&
        !initialAnswersCopy[questionProps.questionId] &&
        questionProps.defaultValue
      ) {
        initialAnswersCopy[questionProps.questionId] = questionProps.defaultValue
      }
      return initialAnswersCopy
    }, sectionAnswers)
    return initialValues
  }, [questionsComponentProps, sectionAnswers])

  // This effect is needed to make sure that the information is sent to the redux
  // state even if the user doesn't touch any field (Ex. all prefilled)
  useEffect(() => {
    updateSurveyAnswers(currentStep, initialValues)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- We only want to run this effect once
  }, [])

  const handleSubmit = (values: any) => {
    // If all the default values of the section's questions
    // are good the user will go directly to the next step so
    // we need to make sure we update the answers in that case
    updateSurveyAnswers(currentStep, values)
    if (onNextSection) {
      onNextSection()
    }
  }

  return (
    <Form
      name="section_form"
      form={form}
      // Update survey answers on each field update to verify conditionals questions.
      // Since we want to keep the previous answers we use allValues parameter instead of
      // changedValues
      onValuesChange={(changedValues: any, allValues: Partial<CumulatedAnswers>) => {
        updateSurveyAnswers(currentStep, allValues)
      }}
      onFinish={handleSubmit}
      layout="vertical"
      hideRequiredMark
      className="u-margin-top--large-3"
      initialValues={initialValues}
      scrollToFirstError={{
        behavior: (actions) =>
          actions.forEach(({ el, top, left }) => {
            el.scrollTop = top - 100
            el.scrollLeft = left
          })
      }}
    >
      {questionsComponentProps.map((questionProps) =>
        renderQuestion({
          ...questionProps,
          fieldName: questionProps.questionId,
          form,
          upsertQuestions: upsertSurveyQuestions
        })
      )}
      <Form.Item>
        {isXSmall ? (
          // Extra small screen Button
          <MobileButton isLoading={isLoading} completionPercentage={completionPercentage} currentStep={currentStep} />
        ) : (
          // Small and Up Button
          <div
            className={cx(
              'u-flex-align-center',
              'u-flex-justify-end',
              'u-padding-top--medium',
              styles['button-container']
            )}
          >
            <AppButton type="primary" htmlType="submit" aria-describedby="progress" loading={isLoading}>
              <span className="u-flex-align-center">
                {nextSectionText} <FontAwesomeIcon icon={faArrowRight} className="u-margin-left--small" />
              </span>
              <AriaPercentageCompletion completionPercentage={completionPercentage} />
            </AppButton>
          </div>
        )}
      </Form.Item>
    </Form>
  )
}

const mapStateToProps = (state: any, { currentStep }: { currentStep: number }) => ({
  cumulatedAnswers: selectors.getAnswers(state),
  prefilledData: selectors.getPrefilledData(state),
  sectionAnswers: selectors.makeGetSectionAnswers(currentStep)(state),
  questionsByRefKey: selectors.getQuestionsByIdOrRefKey(state)
})

const mapDispatchToProps = {
  updateSurveyAnswers,
  upsertSurveyQuestions
}

export default connect(mapStateToProps, mapDispatchToProps)(SectionForm)

interface SectionFormProps {
  initialValidation?: boolean
  questions: QuestionInterface[]
  onNextSection: () => void
  upsertSurveyQuestions: (questions: QuestionInterface[]) => void
  updateSurveyAnswers: (section: number, answers: Record<string, unknown>) => void
  nextSectionText: React.ReactNode
  currentStep: number
  prefilledData: PrefilledData
  cumulatedAnswers: CumulatedAnswers
  questionsByRefKey: Record<string, QuestionInterface>
  sectionAnswers: Record<string, unknown>
  completionPercentage: number
  isLoading?: boolean
}
